import {
  cleanNumber,
  formatNumber,
  incrementNumber,
  parseFormattedNumber,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { Colors } from '../../themes/colors';
import { TextInput as CropCircleTextInput } from '../crop-circle/Components/TextInput/TextInput';
import { AddIcon } from '../crop-circle/icons/addIcon';
import { RemoveIcon } from '../crop-circle/icons/removeIcon';
import { tokens } from '../crop-circle/tokens';
import { StyledTextInputProps } from './controlledTextInput';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type MaskedNumberInputProps = Omit<
  StyledTextInputProps,
  'onChangeText' | 'value'
> & {
  value: number | string | null;
  onChangeText: (value: number | string) => void;
  decimalPlaces?: number;
  mode?: 'outlined' | 'flat' | undefined;
  hideClear?: boolean;
  selectionColor?: string;
  cursorColor?: string;
  outlineStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<TextStyle>;
  showStepper?: boolean;
  disabled?: boolean;
  label?: string;
  stepperButtonStyle?: StyleProp<ViewStyle>;
  stepperIconColor?: string;
};

export const MaskedNumberInput = ({
  value,
  onChangeText,
  onBlur,
  onFocus: onFocusProp,
  decimalPlaces = 0,
  hideClear = false,
  ...rest
}: MaskedNumberInputProps) => {
  const isFocusedRef = useRef(false);
  const [rawText, setRawText] = useState(() =>
    value != null && value !== '' && value !== '0'
      ? formatNumber(value, decimalPlaces)
      : ''
  );

  useEffect(() => {
    if (isFocusedRef.current) return;
    if (value == null || value === '' || value === '0') {
      setRawText('');
    } else {
      setRawText(formatNumber(value, decimalPlaces));
    }
  }, [value, decimalPlaces]);

  const handleChangeText = useCallback(
    (text: string) => {
      const cleaned = cleanNumber(text, decimalPlaces, false);
      setRawText(cleaned);
      const parsed = parseFormattedNumber(cleaned);
      if (cleaned === '') {
        onChangeText('');
      } else {
        onChangeText(decimalPlaces === 0 ? Math.round(parsed) : parsed);
      }
    },
    [decimalPlaces, onChangeText]
  );

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
    onFocusProp?.();
  }, [onFocusProp]);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    const parsed = parseFormattedNumber(rawText);
    const formatted = formatNumber(parsed, decimalPlaces);
    setRawText(formatted);
    onBlur?.();
  }, [rawText, decimalPlaces, onBlur]);

  const placeholder = rest.placeholder
    ? rest.placeholder
    : decimalPlaces > 0
    ? `0.${Array(decimalPlaces).fill('0').join('')}`
    : '0';

  // Same CropCircleTextInput for web and mobile so number input style matches
  return (
    <>
      {rest.showStepper && (
        <ShowStepperIconButton
          iconName="minus"
          onPress={() =>
            handleChangeText(
              `${incrementNumber(
                `${cleanNumber(rawText, decimalPlaces, true)}`,
                -1,
                decimalPlaces
              )}`
            )
          }
          isLeft={false}
          disabled={rest.disabled}
          buttonStyle={rest.stepperButtonStyle}
          iconColor={rest.stepperIconColor}
        />
      )}
      {rest.label ? (
        <CropCircleTextInput
          label={rest.label}
          value={rawText}
          onChangeText={handleChangeText}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={rest.disabled}
          keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
          height={rest.height || 48}
          backgroundColor={Colors.role.surfaceHover}
          hideBorder
          clearable={!hideClear && !rest.showStepper}
        />
      ) : (
        <View
          style={[
            styles.container,
            { minHeight: rest.height || 48 },
            rest.showStepper && styles.containerInStepper,
          ]}
        >
          <CropCircleTextInput
            value={rawText}
            onChangeText={handleChangeText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={rest.disabled}
            keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
            height={rest.height || 48}
            backgroundColor="transparent"
            hideBorder
            clearable={!hideClear && !rest.showStepper}
            inputStyle={StyleSheet.flatten([
              styles.inputNoPadding,
              rest.showStepper && styles.inputStepperCentered,
            ])}
            rightIconStyle={styles.rightIconCentered}
          />
        </View>
      )}
      {rest.showStepper && (
        <ShowStepperIconButton
          iconName="plus"
          onPress={() =>
            handleChangeText(
              `${incrementNumber(
                `${cleanNumber(rawText, decimalPlaces, true)}`,
                +1,
                decimalPlaces
              )}`
            )
          }
          isLeft
          disabled={rest.disabled}
          buttonStyle={rest.stepperButtonStyle}
          iconColor={rest.stepperIconColor}
        />
      )}
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: role.surfaceHover,
    borderWidth: 0,
    minHeight: 48,
    paddingHorizontal: tokens.spacing.lg,
    justifyContent: 'center',
    overflow: 'hidden',
    flex: 4,
  },
  containerInStepper: {
    borderRadius: 0,
  },
  inputNoPadding: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    // Keep right padding for icon positioning - PaperTextInput.Icon uses contentStyle paddingRight
    // Container has paddingHorizontal: 16px, so we want icon at TextInput's right edge (16px from container)
    paddingRight: 0,
    textAlignVertical: 'center',
    // Ensure vertical centering for both PaperTextInput and RNTextInput
    ...(Platform.OS !== 'web' && {
      includeFontPadding: false,
    }),
  },
  inputStepperCentered: {
    textAlign: 'center',
  },
  rightIconCentered: {
    // Override contentSingleLine padding to center the icon vertically
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: 0,
    // Center the icon - PaperTextInput.Icon style accepts TextStyle
    textAlignVertical: 'center',
  },
}));

const ShowStepperIconButton = (props: {
  iconName: string;
  onPress: () => void;
  isLeft: boolean;
  disabled?: boolean;
  buttonStyle?: StyleProp<ViewStyle>;
  iconColor?: string;
}) => {
  const IconComponent = props.iconName === 'minus' ? RemoveIcon : AddIcon;
  const iconColor = props.iconColor ?? Colors.role.textSecondary;
  return (
    <Pressable
      style={({
        pressed,
        hovered,
      }: {
        pressed: boolean;
        hovered?: boolean;
      }) => [
        styledCropCircle.counterButton,
        props.buttonStyle,
        hovered && styledCropCircle.counterButtonHovered,
        pressed && styledCropCircle.counterButtonPressed,
      ]}
      onPress={props.onPress}
      disabled={props.disabled}
    >
      <IconComponent size={16} color={iconColor} />
    </Pressable>
  );
};

export const styledCropCircle = themedStyleSheet((role) => ({
  counterButton: {
    width: 48,
    height: '100%',
    backgroundColor: role.surfaceHover,
    borderRadius: 0,
    borderWidth: 0,
    margin: 0,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && { userSelect: 'none', cursor: 'pointer' }),
  },
  counterButtonHovered: {
    backgroundColor: role.surfaceActive,
  },
  counterButtonPressed: {
    opacity: 0.6,
  },
}));
